/**
 * `SCR-MEAS` (13 states), `SCR-REMEASURE` (5), `SCR-PRIVACY` (3).
 * FLOWS_SPEC §4. OWNER: web-frontend-engineer.
 *
 * RULING-001: A RECORD, NOT A FORM. The ledger is rendered by
 * `MeasurementRecordView`, which contains no input element to disable. The
 * remeasure screen books time with a tailor; it never opens an editor and never
 * suggests a number, and the server rejects the whole request if a measurement
 * field appears at any depth (P-MEAS-05).
 */

import { useState } from 'react';
import { t } from '@pt/copy';
import { api } from '../lib/api.ts';
import { useResource, useSubmission } from '../lib/resource.ts';
import { dateInWords } from '@pt/view';
import { AppShell } from '../app/AppShell.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import { MeasurementRecordView } from '../components/record.tsx';
import { Button, FactLine, Statement, TextInput } from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';

export function MeasurementsScreen({ recordId }: { recordId?: string }) {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const records = useResource(() => api.measurements(), []);

  const data = records.data;
  const all = data?.records ?? [];
  const shown =
    recordId === undefined
      ? all.find((record) => record.recordId === data?.currentRecordId) ?? all[0]
      : all.find((record) => record.recordId === recordId);
  const successor =
    shown?.supersededBy == null
      ? null
      : (all.find((record) => record.recordId === shown.supersededBy) ?? null);

  const title = data?.clientName ?? t('meas.heading');

  return (
    <AppShell
      title={title}
      width="ledger"
      banner={records.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {records.phase === 'skeleton' || records.phase === 'idle' ? (
        <LoadingRegion rows={12} variant="ledger" slow={records.slow} label={t('meas.heading')} />
      ) : null}

      {records.phase === 'error' ? <FetchError onRetry={records.reload} /> : null}

      {/* ST-MEAS-NORECORD — no numbers, no placeholders, no zeroes, and no
          invitation to enter anything. */}
      {records.phase === 'ready' && shown === undefined ? (
        <section>
          <Statement>{t('meas.norecord.statement')}</Statement>
          <div className="pt-actions">
            <Button onClick={() => navigate('/measurements/remeasure')}>
              {t('meas.norecord.action')}
            </Button>
          </div>
        </section>
      ) : null}

      {shown === undefined ? null : (
        <MeasurementRecordView
          record={shown}
          clientName={title}
          tailorName={tailor?.displayName ?? shown.authoredByName}
          successorTakenAt={successor?.takenAt ?? null}
          onOpenSuccessor={() =>
            successor === null ? undefined : navigate(`/measurements/${successor.recordId}`)
          }
          lastOpened={data?.lastOpened ?? null}
          history={all}
          onOpenRecord={(id) => navigate(`/measurements/${id}`)}
          onRemeasure={() => navigate('/measurements/remeasure')}
          onPrivacyCopy={() => navigate('/privacy?ask=copy')}
          onPrivacyErase={() => navigate('/privacy?ask=erase')}
          onPrivacyFull={() => navigate('/privacy')}
        />
      )}
    </AppShell>
  );
}

/** `SCR-REMEASURE` — books time with a tailor. NO measurement field of any kind. */
export function RemeasureScreen() {
  const { contact, tailor } = useHouse();
  const [timing, setTiming] = useState('');
  const [recordedAt, setRecordedAt] = useState<string | null>(null);
  const [existingAt, setExistingAt] = useState<string | null>(null);
  const submission = useSubmission();

  return (
    <AppShell title={t('remeas.default.heading')} width="ledger">
      <Statement>{t('remeas.default.statement')}</Statement>

      {existingAt !== null ? (
        <Statement>
          {t('remeas.existing.statement', {
            requestedDate: dateInWords(existingAt),
            tailorName: tailor?.displayName ?? '',
          })}
        </Statement>
      ) : null}

      {recordedAt !== null ? (
        <Statement>
          {t('remeas.recorded.statement', {
            recordedDate: dateInWords(recordedAt),
            tailorName: tailor?.displayName ?? '',
            contactName: contact?.displayName ?? '',
          })}
        </Statement>
      ) : (
        <>
          {submission.rejection === null ? null : (
            <FetchError whatKey="remeas.error.what" />
          )}
          {submission.offline ? <Statement>{t('offline.writeBlocked')}</Statement> : null}
          <TextInput
            label={t('remeas.default.field.label')}
            value={timing}
            onChange={setTiming}
            multiline
          />
          <div className="pt-actions">
            <Button
              variant="primary"
              busy={submission.busy}
              busyLabel={t('remeas.busy.action')}
              onClick={() => {
                void submission
                  .run(() => api.requestRemeasure({ preferredTiming: timing }))
                  .then((result) => {
                    if (result === null) return;
                    if (result.existingAt !== undefined) setExistingAt(result.existingAt);
                    else setRecordedAt(result.recordedAt);
                  });
              }}
            >
              {t('remeas.default.action')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}

/** `SCR-PRIVACY` — the substantiation layer behind `ST-MEAS-PRIVACY` ([T-03]). */
export function PrivacyScreen() {
  const { contact } = useHouse();
  const [copyRecordedAt, setCopyRecordedAt] = useState<string | null>(null);
  const [eraseRecordedAt, setEraseRecordedAt] = useState<string | null>(null);
  const submission = useSubmission();

  return (
    <AppShell title={t('priv.statement.heading')} width="ledger">
      <div className="pt-prose">
        <p className="type-role-body">{t('priv.statement.p1')}</p>
        <p className="type-role-body pt-block">{t('priv.statement.p2')}</p>
        <p className="type-role-body pt-block">{t('priv.statement.p3')}</p>
        <p className="type-role-body pt-block">{t('priv.statement.p4')}</p>
        <p className="type-role-body pt-block">{t('priv.statement.p5')}</p>
      </div>

      {/* Both routes at EQUAL WEIGHT ([T-31]). Neither implies immediacy. */}
      <section className="pt-section">
        <h2 className="pt-cutsheet__blockTitle">{t('priv.copy.heading')}</h2>
        <Statement>{t('priv.copy.body')}</Statement>
        {copyRecordedAt === null ? (
          <div className="pt-actions">
            <Button
              busy={submission.busy}
              busyLabel={t('remeas.busy.action')}
              onClick={() => {
                void submission
                  .run(() => api.privacyRequest({ kind: 'export' }))
                  .then((result) => result && setCopyRecordedAt(result.recordedAt));
              }}
            >
              {t('priv.copy.action')}
            </Button>
          </div>
        ) : (
          <FactLine>
            {t('priv.copy.recorded', {
              recordedDate: dateInWords(copyRecordedAt),
              contactName: contact?.displayName ?? '',
            })}
          </FactLine>
        )}
      </section>

      <section className="pt-section">
        <h2 className="pt-cutsheet__blockTitle">{t('priv.erase.heading')}</h2>
        <Statement>{t('priv.erase.body')}</Statement>
        {eraseRecordedAt === null ? (
          <div className="pt-actions">
            <Button
              busy={submission.busy}
              busyLabel={t('remeas.busy.action')}
              onClick={() => {
                void submission
                  .run(() => api.privacyRequest({ kind: 'erasure' }))
                  .then((result) => result && setEraseRecordedAt(result.recordedAt));
              }}
            >
              {t('priv.erase.action')}
            </Button>
          </div>
        ) : (
          <FactLine>
            {t('priv.erase.recorded', {
              recordedDate: dateInWords(eraseRecordedAt),
              contactName: contact?.displayName ?? '',
            })}
          </FactLine>
        )}
      </section>
    </AppShell>
  );
}
